import React, { useState } from "react";
import { cn } from "../../utils/cn";

const Input = React.forwardRef(({
    className,
    type = "text",
    label,
    description,
    error,
    required = false,
    id,
    floating = false,
    ...props
}, ref) => {
    const inputId = id || `input-${Math.random()?.toString(36)?.substr(2, 9)}`;
    const [isFocused, setIsFocused] = useState(false);
    const hasValue = props?.value !== undefined ? Boolean(props?.value) : false;

    const baseInputClasses = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

    if (type === "checkbox") {
        return (
            <input
                type="checkbox"
                className={cn(
                    "h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                    className
                )}
                ref={ref}
                id={inputId}
                {...props}
            />
        );
    }

    if (type === "radio") {
        return (
            <input
                type="radio"
                className={cn(
                    "h-4 w-4 rounded-full border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                    className
                )}
                ref={ref}
                id={inputId}
                {...props}
            />
        );
    }

    // Floating label variant
    if (floating && label) {
        const lifted = isFocused || hasValue;
        return (
            <div className="space-y-1">
                <div className="relative group">
                    {/* Background gradient pulse on focus */}
                    <div
                        className={cn(
                            "absolute inset-0 rounded-xl transition-all duration-300 pointer-events-none",
                            isFocused
                                ? "bg-gradient-to-r from-primary/8 via-primary/4 to-transparent opacity-100" :"opacity-0"
                        )}
                    />
                    <input
                        type={type}
                        className={cn(
                            "peer w-full px-4 pt-6 pb-2 rounded-xl border-2 bg-background text-foreground",
                            "placeholder:text-transparent",
                            "focus:outline-none focus:ring-2 focus:ring-primary/20",
                            "transition-all duration-200",
                            isFocused
                                ? "border-primary shadow-[0_0_0_3px_hsl(var(--primary)/0.12)]"
                                : "border-border hover:border-border/80",
                            error && "border-destructive focus:ring-destructive/20",
                            className
                        )}
                        ref={ref}
                        id={inputId}
                        placeholder={label}
                        onFocus={(e) => { setIsFocused(true); props?.onFocus?.(e); }}
                        onBlur={(e) => { setIsFocused(false); props?.onBlur?.(e); }}
                        {...props}
                    />
                    {/* Floating label */}
                    <label
                        htmlFor={inputId}
                        className={cn(
                            "absolute left-4 transition-all duration-200 pointer-events-none select-none",
                            lifted
                                ? "top-2 text-xs font-semibold" :"top-1/2 -translate-y-1/2 text-sm",
                            isFocused
                                ? "text-primary"
                                : lifted
                                    ? "text-muted-foreground"
                                    : "text-muted-foreground",
                            error && "text-destructive"
                        )}
                    >
                        {label}
                        {required && <span className="text-destructive ml-1">*</span>}
                    </label>
                </div>
                {description && !error && (
                    <p className="text-sm text-muted-foreground px-1">{description}</p>
                )}
                {error && (
                    <p className="text-sm text-destructive px-1">{error}</p>
                )}
            </div>
        );
    }

    // Standard variant (unchanged)
    return (
        <div className="space-y-2">
            {label && (
                <label
                    htmlFor={inputId}
                    className={cn(
                        "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
                        error ? "text-destructive" : "text-foreground"
                    )}
                >
                    {label}
                    {required && <span className="text-destructive ml-1">*</span>}
                </label>
            )}

            <input
                type={type}
                className={cn(
                    baseInputClasses,
                    error && "border-destructive focus-visible:ring-destructive",
                    className
                )}
                ref={ref}
                id={inputId}
                {...props}
            />

            {description && !error && (
                <p className="text-sm text-muted-foreground">
                    {description}
                </p>
            )}

            {error && (
                <p className="text-sm text-destructive">
                    {error}
                </p>
            )}
        </div>
    );
});

Input.displayName = "Input";

export default Input;